import React from 'react';
import { Settings as SettingsIcon, Clock, Palette, Volume2, Bell, X, Crown, LogOut } from 'lucide-react';
import * as Collapsible from '@radix-ui/react-collapsible';
import * as Switch from '@radix-ui/react-switch';
import * as Slider from '@radix-ui/react-slider';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { useTimer } from '../../context/TimerContext';
import type { ThemeMode } from '../../types/timer';
import { PremiumButton } from '../Premium/PremiumButton';
import { AuthSection } from '../Auth/AuthSection';
import { AudioSettings } from './AudioSettings';
import { PresetsSection } from './PresetsSection';
import { SessionHistorySection } from './SessionHistory';
import { signOut, getSession } from '../../lib/auth';

interface SettingsPanelProps {
  onClose: () => void;
  onShowPremium: () => void;
}

interface SettingsSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

function SettingsSection({ title, icon, children }: SettingsSectionProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <Collapsible.Root className="w-full" open={open} onOpenChange={setOpen}>
      <Collapsible.Trigger className="flex items-center w-full p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
        <div className="flex items-center flex-1">
          {icon}
          <span className="ml-3 font-medium text-gray-900 dark:text-white">{title}</span>
        </div>
        <div className={`transform transition-transform ${open ? 'rotate-180' : ''}`}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </Collapsible.Trigger>
      <Collapsible.Content className="p-4 bg-gray-50 dark:bg-gray-800">
        {children}
      </Collapsible.Content>
    </Collapsible.Root>
  );
}

function TimeInput({ value, onChange, label }: { value: number; onChange: (value: number) => void; label: string }) {
  const minutes = Math.floor(value / 60);

  return (
    <div className="flex items-center justify-between mb-4">
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
      <div className="flex items-center space-x-2">
        <Slider.Root
          className="relative flex items-center select-none touch-none w-32 h-5"
          value={[minutes]}
          onValueChange={(values) => onChange(values[0] * 60)}
          max={60}
          min={1}
          step={1}
        >
          <Slider.Track className="bg-gray-200 dark:bg-gray-700 relative grow rounded-full h-2">
            <Slider.Range className="absolute bg-indigo-600 dark:bg-indigo-500 rounded-full h-full" />
          </Slider.Track>
          <Slider.Thumb
            className="block w-6 h-6 bg-white border-2 border-indigo-600 dark:border-indigo-500 shadow-lg rounded-full hover:bg-gray-50 focus:outline-none"
          />
        </Slider.Root>
        <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[3ch]">{minutes}m</span>
      </div>
    </div>
  );
}

function PremiumBadge() {
  const { refreshAccountState } = useTimer();
  // Premium is visitor-scoped: it can outlive a session (e.g. an anonymous
  // purchase). Only show the logout button when there is actually a session.
  const [signedIn, setSignedIn] = React.useState(Boolean(getSession()));
  const session = getSession();
  const signedInEmail = signedIn ? session?.user?.email : null;

  const handleLogout = async () => {
    await signOut();
    setSignedIn(false);
    // Re-evaluate entitlement from the visitor path — the visitor may still
    // be premium (anonymous purchase), so rely on the server, not the session.
    await refreshAccountState();
  };

  return (
    <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
      <div>
        <div className="flex items-center bg-gradient-to-r from-amber-400 to-amber-600 text-white py-2 px-4 rounded-full shadow-lg">
          <Crown className="w-5 h-5 mr-2" />
          <span className="font-medium">Premium User</span>
        </div>
        {signedInEmail && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-1">
            Signed in as {signedInEmail}
          </p>
        )}
      </div>
      {signedIn && (
        <button
          onClick={handleLogout}
          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
          aria-label="Log out"
        >
          <LogOut className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}

function ThemeButton({ value, label, isSelected }: { value: string; label: string; isSelected: boolean }) {
  return (
    <div
      className={`flex-1 first:rounded-l-md last:rounded-r-md border-y border-r first:border-l border-gray-300 dark:border-gray-600 ${
        isSelected 
          ? 'bg-indigo-600 text-white border-indigo-600 dark:bg-indigo-500 dark:border-indigo-500' 
          : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
      }`}
    >
      <RadioGroup.Item
        value={value}
        className="w-full px-4 py-2 text-sm font-medium cursor-pointer focus:outline-none focus:ring-0"
      >
        {label}
      </RadioGroup.Item>
    </div>
  );
}

export function SettingsPanel({ onClose, onShowPremium }: SettingsPanelProps) {
  const { settings, updateSettings, state } = useTimer();

  const handleThemeChange = (value: ThemeMode) => {
    updateSettings({ theme: value });
  };

  const handleUpgradeClick = () => {
    onClose();
    setTimeout(() => onShowPremium(), 100);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50" onClick={onClose}>
      <div 
        className="absolute right-0 top-0 bottom-0 w-80 bg-white dark:bg-gray-900 shadow-lg grid grid-rows-[auto_1fr_auto]"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex-shrink-0 p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div className="flex items-center">
            <SettingsIcon className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            <h2 className="ml-2 text-lg font-semibold text-gray-900 dark:text-white">Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
            aria-label="Close settings"
          >
            <X className="w-5 h-5 text-gray-700 dark:text-gray-300" />
          </button>
        </div>

        <div className="overflow-y-auto min-h-0 scrollable">
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            <SettingsSection title="Timer" icon={<Clock className="w-5 h-5 text-gray-700 dark:text-gray-300" />}>
              <TimeInput
                label="Focus Duration"
                value={settings.focusDuration}
                onChange={(value) => updateSettings({ focusDuration: value })}
              />
              <TimeInput
                label="Short Break"
                value={settings.shortBreakDuration}
                onChange={(value) => updateSettings({ shortBreakDuration: value })}
              />
              <TimeInput
                label="Long Break"
                value={settings.longBreakDuration}
                onChange={(value) => updateSettings({ longBreakDuration: value })}
              />
              <div className="flex items-center justify-between mt-4">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Auto-advance Timer</label>
                <Switch.Root
                  checked={settings.autoAdvance}
                  onCheckedChange={(checked) => updateSettings({ autoAdvance: checked })}
                  className="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full relative data-[state=checked]:bg-indigo-600 dark:data-[state=checked]:bg-indigo-500 transition-colors"
                >
                  <Switch.Thumb className="block w-4 h-4 bg-white dark:bg-gray-200 rounded-full shadow-lg transition-transform duration-100 translate-x-1 data-[state=checked]:translate-x-6" />
                </Switch.Root>
              </div>
            </SettingsSection>

            <SettingsSection title="Display" icon={<Palette className="w-5 h-5 text-gray-700 dark:text-gray-300" />}>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 block">Theme</label>
                  <RadioGroup.Root
                    className="flex w-full"
                    value={settings.theme}
                    onValueChange={handleThemeChange}
                  >
                    <ThemeButton value="light" label="Light" isSelected={settings.theme === 'light'} />
                    <ThemeButton value="dark" label="Dark" isSelected={settings.theme === 'dark'} />
                    <ThemeButton value="system" label="System" isSelected={settings.theme === 'system'} />
                  </RadioGroup.Root>
                </div>
              </div>
            </SettingsSection>

            <SettingsSection title="Audio" icon={<Volume2 className="w-5 h-5 text-gray-700 dark:text-gray-300" />}>
              <AudioSettings
                soundEnabled={settings.soundEnabled}
                onSoundEnabledChange={(enabled) => updateSettings({ soundEnabled: enabled })}
              />
            </SettingsSection>

            <SessionHistorySection onUpgrade={handleUpgradeClick} />

            {state.isPremium && (
              <SettingsSection title="Presets" icon={<Clock className="w-5 h-5 text-gray-700 dark:text-gray-300" />}>
                <PresetsSection />
              </SettingsSection>
            )}

            <SettingsSection title="Notifications" icon={<Bell className="w-5 h-5 text-gray-700 dark:text-gray-300" />}>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Notifications</label>
                  <Switch.Root
                    checked={settings.notificationsEnabled}
                    onCheckedChange={(checked) => {
                        updateSettings({ notificationsEnabled: checked });
                        // Request permission only if enabling and permission is default
                        if (checked && 'Notification' in window && Notification.permission === 'default') {
                            Notification.requestPermission().then(permission => {
                                // Optional: Handle denied permission (e.g., update UI)
                                if (permission !== 'granted') {
                                    updateSettings({ notificationsEnabled: false }); // Turn setting back off if denied
                                    console.warn('Notification permission denied.');
                                    // You might want to show a message to the user here
                                }
                            });
                        }
                    }}
                    className="w-11 h-6 bg-gray-200 dark:bg-gray-700 rounded-full relative data-[state=checked]:bg-indigo-600 dark:data-[state=checked]:bg-indigo-500 transition-colors"
                  >
                    <Switch.Thumb className="block w-4 h-4 bg-white dark:bg-gray-200 rounded-full shadow-lg transition-transform duration-100 translate-x-1 data-[state=checked]:translate-x-6" />
                  </Switch.Root>
                </div>
              </div>
            </SettingsSection>

            {/* Premium/Auth section - shown in scroll on mobile, fixed at bottom on desktop */}
            <div className="lg:hidden">
              {state.isPremium ? (
                <PremiumBadge />
              ) : (
                <>
                  <PremiumButton onClick={handleUpgradeClick} />
                  <AuthSection />
                </>
              )}
            </div>
          </div>
        </div>

        {/* Desktop-only footer */}
        <div className="hidden lg:block">
          {state.isPremium ? (
            <PremiumBadge />
          ) : (
            <>
              <PremiumButton onClick={handleUpgradeClick} />
              <AuthSection />
            </>
          )}
        </div>
      </div>
    </div>
  );
}